import { useState, useEffect, useRef, useCallback } from "react";
import { useUser } from "src/shared/context/UserContext";
import { getOrder, deleteOrder } from "../services/servicesv2";
import { Order, PaginationData, UseOrdersProps } from "../services/interfacev2";
import { notifyError, notifySuccess, notifyWarning } from "src/assets/toastService";

export const useOrders = ({ initialPage = 0, initialRowsPerPage = 10 }: UseOrdersProps) => {
  const [orders, setOrders] = useState<Order[]>([]);

  const [loadings, setLoadings] = useState(false);
  const [page, setPage] = useState(initialPage);
  const { userConfigration } = useUser();
  const [rowsPerPage, setRowsPerPage] = useState(userConfigration?.tableDefaultSize || initialRowsPerPage);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState("Today");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedDate2, setSelectedDate2] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paginationData, setPaginationData] = useState<PaginationData>({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: initialRowsPerPage,
  });
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  interface OrderParams {
  dateFilter: string;
  searchBy: string;
  startDate?: string;
  endDate?: string;
}

  const getDateRange = useCallback(
    (filterOption = selectedOption) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let fromDate = new Date(today);
      let toDate = new Date(today);

      switch (filterOption) {
        case "Yesterday":
          fromDate.setDate(today.getDate() - 1);
          toDate.setDate(today.getDate() - 1);
          break;
        case "Last Week":
          fromDate.setDate(today.getDate() - 6);
          break;
        case "Last Month":
          fromDate.setDate(today.getDate() - 29);
          break;
        case "180 days":
          fromDate.setDate(today.getDate() - 179);
          break;
        case "CustomizeDate":
          if (selectedDate && selectedDate2) {
            fromDate = new Date(selectedDate);
            toDate = new Date(selectedDate2);
          } else {
            fromDate = new Date(today);
            toDate = new Date(today);
          }
          break;
      }
      const formatDate = (date: Date) => {
        return date.getFullYear() + "-" + String(date.getMonth() + 1).padStart(2, "0") + "-" + String(date.getDate()).padStart(2, "0");
      };

      return {
        startDate: formatDate(fromDate),
        endDate: formatDate(toDate),
      };
    },
    [selectedDate, selectedDate2, selectedOption],
  );

  const getDateRangeForExport = useCallback((filterOption?: string) => {
    const { startDate, endDate } = getDateRange(filterOption);
    return {
      from: startDate,
      to: endDate,
    };
  }, [getDateRange]);


  const fetchOrders = useCallback(
    async (pageNumber = page + 1, pageSize = rowsPerPage, search = searchTerm, filterOption = selectedOption, status = statusFilter) => {
      try {
        setLoadings(true);

        if (filterOption === "CustomizeDate" && (!selectedDate || !selectedDate2)) {
          setOrders([]);
          setPaginationData({ totalRecords: 0, totalPages: 0, currentPage: 1, pageSize });
          return;
        }

        const params: OrderParams  = {
          dateFilter: (() => {
            const lower = filterOption.toLowerCase();
            if (lower === "lastweek") return "7";
            if (lower === "lastmonth") return "30";
            if (lower === "180days") return "180";
            if (lower === "customizedate") return "custom";
            return (lower);
          })(),
          searchBy: search, // This will now include bill numbers since sanitization allows numbers
        };

        if (filterOption === "CustomizeDate") {
          params.startDate = selectedDate; 
          params.endDate = selectedDate2; 
        }

        const response = await getOrder(
          "", // id
          pageNumber, // page
          pageSize, // limit
          params.startDate as string, // ✅ Type assertion
          params.endDate as string,   // ✅ Type assertion
          "", // name
          "", // staff
          "", // sortField
           status || "", // status
          params, // ✅ params (correct position)
        );

        if (response.data?.result) {
          setOrders(response.data.result);
          setPaginationData({
            totalRecords: response.data.totalRecords || 0,
            totalPages: response.data.totalPages || 0,
            currentPage: response.data.currentPage || 1,
            pageSize: response.data.pageSize || pageSize,
          });
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
        notifyError("Failed to fetch orders");
      } finally {
        setLoadings(false);
      }
    },
    [page, rowsPerPage, searchTerm, selectedOption, selectedDate, selectedDate2, statusFilter],
  );

  const handleDeleteOrder = useCallback(
    async (orderId: string) => {
      try {
        await deleteOrder(orderId);
        notifySuccess("Order deleted successfully");
        fetchOrders(page + 1, rowsPerPage, searchTerm);
      } catch {
        notifyError("Failed to delete order");
      }
    },
    [fetchOrders, page, rowsPerPage, searchTerm],
  );

  // FIXED: Allow numbers in search (for bill numbers)
  const handleSearch = (value: string) => {
    // Keep the original value including leading zeros
    // Just trim whitespace
    const searchValue = value.trim();
    setSearchTerm(searchValue);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setPage(0);
      // Pass the search value directly without modification
      fetchOrders(1, rowsPerPage, searchValue, selectedOption);
    }, 500);
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
    fetchOrders(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
    fetchOrders(1, newRows, searchTerm, selectedOption);
  };

  const handleFilterChange = (value: string) => {
    setSelectedOption(value);
    setPage(0);

    if (value !== "CustomizeDate") {
      setSelectedDate("");
      setSelectedDate2("");
      fetchOrders(1, rowsPerPage, searchTerm, value);
    } else {
      setOrders([]);
      setPaginationData({ totalRecords: 0, totalPages: 0, currentPage: 1, pageSize: rowsPerPage });
    }
  };

  const applyCustomDateFilter = () => {
    if (!selectedDate || !selectedDate2) {
      notifyWarning("Please select both start and end dates");
      return;
    }
    if (new Date(selectedDate) > new Date(selectedDate2)) {
      notifyWarning("Start date cannot be after end date");
      return;
    }
    setSelectedOption("CustomizeDate");
    setPage(0);
    fetchOrders(1, rowsPerPage, searchTerm, "CustomizeDate");
  };

   const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setPage(0);
    fetchOrders(1, rowsPerPage, searchTerm, selectedOption, status);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  return {
    orders,
    loadings,
    page,
    rowsPerPage,
    searchTerm,
    selectedOption,
    selectedDate,
    selectedDate2,
    statusFilter,
    paginationData,
    handleSearch,
    handleChangePage,
    handleChangeRowsPerPage,
    handleFilterChange,
    applyCustomDateFilter,
    handleStatusFilterChange,
    setSelectedDate,
    setSelectedDate2,
    fetchOrders,
    getDateRange,
    getDateRangeForExport,
    handleDeleteOrder,
  };
};
